import { NotificationAlerts } from '@terminal/components/notification-alerts/NotificationAlerts';
import type { DisplayToast } from '@terminal/components/toasts-layout/ToastItem';
import { ToastItem } from '@terminal/components/toasts-layout/ToastItem';
import { offsetBetweenToasts } from '@terminal/components/toasts-layout/utils';
import {
  ToastPosition,
  useToasts,
} from '@terminal/stores/use-toasts/useToasts';
import { createRef, RefObject, useEffect, useRef, useState } from 'react';

import styles from './ToastsLayout.module.scss';

export const CONVERSATION_TOAST = 'CONVERSATION_TOAST';

const SCOPES_TO_CLEAR = [CONVERSATION_TOAST];

export const ToastsLayout = () => {
  const { toasts, clearScopedToasts } = useToasts(state => ({
    toasts: state.toasts,
    clearScopedToasts: state.clearScopedToasts,
  }));

  const refs = useRef<Record<string, RefObject<HTMLDivElement | null>>>({});
  const alertsRef = useRef<HTMLDivElement>(null);
  const [alertsHeight, setAlertsHeight] = useState(0);

  useEffect(() => {
    const el = alertsRef.current;
    if (!el) {
      return;
    }
    const observer = new ResizeObserver(() => setAlertsHeight(el.offsetHeight));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  toasts.forEach(t => {
    refs.current[t.id] = refs.current[t.id] || createRef();
  });

  const toastIds = new Set(toasts.map(t => String(t.id)));
  Object.keys(refs.current).forEach(id => {
    if (!toastIds.has(id)) {
      delete refs.current[id];
    }
  });

  const displayToasts: DisplayToast[] = toasts.map((toast, i) => ({
    toast,
    exiting: !!toast.exiting,
    displayIndex: i,
  }));

  const yOffsets = displayToasts.map((currentDt, toastIndex) =>
    displayToasts.slice(0, toastIndex).reduce(
      (offsetY, dt) => {
        const ref = refs.current[dt.toast.id];
        if (ref?.current && dt.toast.position === currentDt.toast.position) {
          return offsetY + ref.current.clientHeight + offsetBetweenToasts;
        }
        return offsetY;
      },
      currentDt.toast.position === ToastPosition.TopRight ? alertsHeight : 0
    )
  );

  const filteredScopes = SCOPES_TO_CLEAR.filter(scope =>
    toasts.some(toast => toast.scope === scope)
  );

  const handleScopedToastsClose = () => {
    filteredScopes.forEach(scope => clearScopedToasts(scope));
  };

  const handleClick = filteredScopes.length
    ? () => handleScopedToastsClose()
    : undefined;

  return (
    <div
      className={styles.toastsWrapper}
      data-testid="toasts-wrapper"
      style={{ pointerEvents: handleClick ? 'all' : 'none' }}
      onClick={handleClick}
    >
      <div
        ref={alertsRef}
        className={styles.alerts}
        onClick={e => e.stopPropagation()}
      >
        <NotificationAlerts />
      </div>
      {displayToasts.map((displayToast, toastIndex) => (
        <ToastItem
          key={displayToast.toast.id}
          ref={refs.current[displayToast.toast.id]}
          displayToast={displayToast}
          offsetY={yOffsets[toastIndex]}
        />
      ))}
    </div>
  );
};
